/**
 * One headline figure with what it is a share of, and the sections that
 * explain it — the concept's segment result panel (`.sb-result`), made the
 * shape every figure panel in the product takes.
 *
 * ## THE FIGURE IS A UNION, NOT A NUMBER
 *
 * Every critical this codebase has shipped on a dashboard was a `0` standing
 * where "not measured" belonged — a bounce rate of 1.55% behind a green badge
 * over mail nobody had reported on. So `figure` is
 *
 *     { kind: 'measured', value, unit? } | { kind: 'absent', node }
 *
 * and the absent branch REQUIRES a node — a `NoFigure` or a `StateChip` — with
 * no default. There is no path through this component that turns "we do not
 * know" into a digit; a caller who has no figure has to say which kind of
 * nothing it is. The rejected alternative was `value: number | null` with a
 * fallback dash, which is the shape that let a `null` be coalesced to `0` one
 * component up.
 *
 * ## THE DENOMINATOR IS THE CALLER'S WORDS
 *
 * `sub` is a slot, not a `total` prop. "of 6,900 contacts", "rows across the
 * ticked lists, before duplicates", "of N contacts (rows)" — what the figure is
 * a share of is product knowledge and differs per screen, and a component that
 * wrote "of N" itself would write it about rows and people alike. The meter is
 * magnitude only (brand or an identity tone): the panel never colours its own
 * figure as a verdict; a verdict goes in `status`, as a Badge with a word.
 *
 * ## BySourceBars: COUNTS, NEVER A POOLED RATE
 *
 * The rows are counts per source or per channel, each with its own "of N".
 * They never add up to a rate across rows. Pooling a rate across channels is
 * exactly how bounce and complaint rates were once computed over every channel
 * and drawn against AWS's email-only thresholds — a workspace sitting on
 * Amazon's 5% line rendered as 1.55%. A row whose count is unknown renders
 * `NoFigure` and no meter, never an empty bar that reads as zero.
 *
 * The grid is the concept's five columns (dot · label · meter · count · of),
 * built from `subgrid` rather than `display: contents`, because `contents` on
 * a `dt`/`dd` has dropped the element's role in shipping browsers and these
 * rows are a description list on purpose: a screen reader hears "Upload, 469
 * of 2,400". The dot's class is a tone class, never `bg-current` —
 * `verify-dashboard` counts `rounded-full bg-current` as Badge dots.
 *
 * ## THE LINE-HEIGHT GOES LAST
 *
 * `cn` is tailwind-merge, which drops a `leading-*` class whenever a font-size
 * class follows it in the same call. The figure's `leading-[1.05]` therefore
 * comes after `FIGURE_SIZE`, never inside the base string before it — the
 * first build had it the other way round and every figure in the product fell
 * back to a 1.5 line-height (slice 66 review). verify-ui-suite pins that the
 * rendered figure carries it.
 *
 * ## A SYMBOL UNIT IS PART OF THE NUMBER
 *
 * A word unit ("595 people") sits beside the figure, smaller and muted. A `%`
 * is not a word: drawn the same way it read as "63  %", detached. So `%`
 * renders attached, at the figure's size and colour, and every other unit is
 * the muted word.
 *
 * ## THE HOLE IT LEAVES
 *
 * No count-up animation and no live dot yet: those belong to rule-based
 * segments, whose counts change while you watch. `live="polite"` is here
 * already for a figure that changes in place, so that day is a prop, not a
 * rewrite.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import type { IdentTone } from '../lib/identity';
import { DOT_CLASS } from '../lib/tone-classes';
import { Card } from './Card';
import { NoFigure, type AbsentFigureState } from './FigureState';
import { Kicker } from './Kicker';
import { Meter } from './Meter';

const FIGURE_SIZE = {
  xl: 'text-[2.2rem] sm:text-[2.6rem]',
  lg: 'text-[2rem]',
  md: 'text-[1.5rem]',
} as const;

export type MetricFigureSize = keyof typeof FIGURE_SIZE;

export type MetricFigure =
  | { readonly kind: 'measured'; readonly value: number; readonly unit?: string }
  | { readonly kind: 'absent'; readonly node: ReactNode };

export interface MetricPanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The kicker's text — "Matches now", "What the import produced". */
  kicker: ReactNode;
  /**
   * Default `'p'`. A panel that stands on its own, with no CardTitle above it,
   * passes a heading level so it stays in the page outline.
   */
  kickerAs?: 'p' | 'h2' | 'h3';
  /** Top-right slot: a Badge or StateChip ("Snapshot"). */
  status?: ReactNode;
  figure: MetricFigure;
  /** Default `'xl'`: 2.6rem (2.2rem below `sm`); `lg` 2rem; `md` 1.5rem. */
  figureSize?: MetricFigureSize;
  /** What the figure is of, in the caller's own words. */
  sub?: ReactNode;
  /** Magnitude only — never a status tone. */
  meter?: { readonly value: number; readonly tone?: 'brand' | IdentTone };
  /** Default `'brand'`: violet hairline over the panel wash. `plain` is the Card skin. */
  variant?: 'brand' | 'plain';
  /** `'polite'` when the figure changes in place. Default `'off'`. */
  live?: 'off' | 'polite';
  /** `MetricSection` blocks. */
  children?: ReactNode;
}

const EN_IN = new Intl.NumberFormat('en-IN');

export function MetricPanel({
  kicker,
  kickerAs = 'p',
  status,
  figure,
  figureSize = 'xl',
  sub,
  meter,
  variant = 'brand',
  live = 'off',
  className,
  children,
  ...rest
}: MetricPanelProps) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <Kicker as={kickerAs}>{kicker}</Kicker>
        {status}
      </div>

      <div aria-live={live === 'off' ? undefined : live} aria-atomic={live === 'off' ? undefined : true}>
        <p
          className={cn(
            'mt-2 mb-0.5 flex items-baseline gap-2 font-semibold tracking-[-0.04em] text-text tabular-nums',
            FIGURE_SIZE[figureSize],
            // Last: see THE LINE-HEIGHT GOES LAST.
            'leading-[1.05]',
          )}
        >
          {figure.kind === 'measured' ? (
            figure.unit === '%' ? (
              <span>{`${EN_IN.format(figure.value)}%`}</span>
            ) : (
              <>
                <span>{EN_IN.format(figure.value)}</span>
                {figure.unit !== undefined && (
                  <span className="text-[0.9rem] font-medium tracking-normal text-muted">{figure.unit}</span>
                )}
              </>
            )
          ) : (
            figure.node
          )}
        </p>
        {sub !== undefined && sub !== null && <p className="text-[0.82rem] text-muted">{sub}</p>}
      </div>

      {meter !== undefined && (
        <Meter value={meter.value} tone={meter.tone ?? 'brand'} size="md" className="mt-2.5 mb-1 w-full" />
      )}

      {children}
    </>
  );

  if (variant === 'plain') {
    return (
      <Card className={cn('min-w-0 p-4', className)} {...rest}>
        {body}
      </Card>
    );
  }

  return (
    <div
      className={cn('min-w-0 rounded-lg border border-brand-b/30 bg-panel-brand p-4', className)}
      {...rest}
    >
      {body}
    </div>
  );
}

export interface MetricSectionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  kicker: ReactNode;
  /** Default `'p'`; `'h3'` when the section belongs in the outline. */
  kickerAs?: 'p' | 'h3';
  children?: ReactNode;
}

/** A titled block inside a MetricPanel: the kicker with its trailing rule, then the content. */
export function MetricSection({ kicker, kickerAs = 'p', className, children, ...rest }: MetricSectionProps) {
  return (
    <div className={cn('mt-4', className)} {...rest}>
      <Kicker as={kickerAs} rule className="mb-2">
        {kicker}
      </Kicker>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* BySourceBars                                                                */
/* -------------------------------------------------------------------------- */

export interface BySourceRow {
  readonly key: string;
  readonly label: ReactNode;
  /** Default `'brand'`. Identity tones for sources; never a status tone. */
  readonly tone?: IdentTone | 'brand';
  /** `null` is an absence: the row shows NoFigure and no meter. */
  readonly count: number | null;
  /**
   * Which absence a `null` count is. Default: not measured. A list known to
   * have imported nothing yet has not gone unmeasured, and a screen reader
   * should not be told it has — the caller says what the dash means.
   */
  readonly absent?: { readonly state: AbsentFigureState; readonly meaning?: string };
  /** What this row's count is out of. `null`/absent prints no "of N". */
  readonly of?: number | null;
}

export interface BySourceBarsProps extends Omit<HTMLAttributes<HTMLDListElement>, 'children'> {
  rows: readonly BySourceRow[];
  /**
   * `share` (default): every track is full width; the fill is count ÷ of, or,
   * where a row has no `of`, count ÷ the largest count shown.
   * `proportional`: each track is as long as its `of` relative to the largest
   * `of` (never under 8%, so a small source is still visible), and the fill is
   * that row's count ÷ of — the concept's by-source reading.
   */
  scale?: 'share' | 'proportional';
}

export function BySourceBars({ rows, scale = 'share', className, ...rest }: BySourceBarsProps) {
  const maxOf = Math.max(1, ...rows.map((row) => row.of ?? 0));
  const maxCount = Math.max(1, ...rows.map((row) => row.count ?? 0));

  return (
    <dl
      className={cn(
        'grid grid-cols-[8px_minmax(70px,auto)_1fr_auto_auto] items-center gap-x-2 gap-y-[7px] text-[0.8rem]',
        className,
      )}
      {...rest}
    >
      {rows.map((row) => {
        const tone = row.tone ?? 'brand';
        const of = row.of ?? null;
        const hasOf = of !== null && of > 0;
        const fill = row.count === null ? 0 : hasOf ? row.count / of : row.count / maxCount;
        const track = scale === 'proportional' && hasOf ? Math.max(8, (of / maxOf) * 100) : 100;

        return (
          <div key={row.key} className="col-span-full grid grid-cols-subgrid items-center">
            <dt className="col-span-2 grid grid-cols-subgrid items-center">
              <span aria-hidden="true" className={cn('size-2 rounded-full', DOT_CLASS[tone])} />
              <span className="min-w-0 truncate text-text">{row.label}</span>
            </dt>
            <dd className="col-span-3 grid grid-cols-subgrid items-center">
              <span className="block min-w-0">
                {row.count !== null && <Meter value={fill} tone={tone} size="md" style={{ width: `${track}%` }} />}
              </span>
              <span className="text-right text-[0.76rem] font-medium text-text tabular-nums">
                {row.count === null ? (
                  <NoFigure state={row.absent?.state ?? 'not_measured'} meaning={row.absent?.meaning} />
                ) : (
                  EN_IN.format(row.count)
                )}
              </span>
              <span className="whitespace-nowrap text-[0.7rem] text-muted tabular-nums">
                {hasOf ? `of ${EN_IN.format(of)}` : ''}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
